import { useEffect, useRef, useState } from "react";
import {
  PhoneCall, Loader2, Play, Pause, Download,
} from "lucide-react";
import { downloadFromUrl } from "@/lib/download";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Field } from "./shared";
import { CallStatusView } from "./callTail";

const LAST_TEST_TO_KEY = "iplogin.outbound.testCall.lastTo";
const POLL_MAX_CONSECUTIVE_ERRORS = 5;

export function TestCallButton({ campaignName }) {
  const [open, setOpen] = useState(false);
  const [wasPrefilled, setWasPrefilled] = useState(false);
  const [to, setTo] = useState(() => {
    try {
      const saved = localStorage.getItem(LAST_TEST_TO_KEY) || "";
      return saved;
    } catch { return ""; }
  });
  useEffect(() => {
    try { if (localStorage.getItem(LAST_TEST_TO_KEY)) setWasPrefilled(true); } catch { /* ignore */ }
  }, []);
  const [message, setMessage] = useState(
    `Hello, this is a test call from ${campaignName || "iplogin Conduit"}. Goodbye.`);
  const [placing, setPlacing] = useState(false);
  const [tracking, setTracking] = useState(null);
  const pollRef = useRef(null);
  const safetyRef = useRef(null);
  const pollErrRef = useRef(0);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (safetyRef.current) { clearTimeout(safetyRef.current); safetyRef.current = null; }
    pollErrRef.current = 0;
  };

  useEffect(() => { return () => stopPolling(); }, []);

  const pollOnce = async (sid) => {
    try {
      const { data } = await api.get(`/outbound/twilio/calls/${sid}`);
      pollErrRef.current = 0;
      setTracking(data);
      if (data.is_terminal) {
        stopPolling();
        if (data.status === "completed") toast.success(`Call completed · ${data.duration_sec || 0}s`);
        else toast.error(`Call ended: ${data.status}`);
      }
    } catch (e) {
      pollErrRef.current += 1;
      const httpStatus = e?.response?.status;
      if (httpStatus === 401 || httpStatus === 403) {
        stopPolling();
        toast.error("Session expired — reload and sign in to resume tailing this call.");
        return;
      }
      if (pollErrRef.current >= POLL_MAX_CONSECUTIVE_ERRORS) {
        stopPolling();
        toast.error("Lost connection to call status — stopped watching.");
      }
    }
  };

  const place = async () => {
    const trimmed = to.trim();
    if (!trimmed.startsWith("+")) {
      toast.error("Number must be in E.164 format, e.g. +15871234567");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/outbound/twilio/test-call", { to: trimmed, message });
      try { localStorage.setItem(LAST_TEST_TO_KEY, trimmed); } catch { /* ignore quota / private-mode */ }
      toast.success(`Call placed · SID ${data.call_sid.slice(0, 10)}…`);
      setTracking({ call_sid: data.call_sid, status: data.status, duration_sec: null, cost_usd: null,
                    events: [{ status: data.status, at: new Date().toISOString() }], is_terminal: false });
      stopPolling();
      pollErrRef.current = 0;
      pollRef.current = setInterval(() => pollOnce(data.call_sid), 2000);
      safetyRef.current = setTimeout(() => stopPolling(), 5 * 60 * 1000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Call could not be placed"); }
    finally { setPlacing(false); }
  };

  const closeDialog = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) { stopPolling(); setTracking(null); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="ai-outbound-test-call-btn">
        <PhoneCall className="h-4 w-4" /> Test call
      </Button>
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="max-w-md" data-testid="ai-outbound-test-call-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneCall className="h-4 w-4 text-primary" /> Place a test call</DialogTitle>
            <DialogDescription>
              {tracking
                ? "Watching call status live. Closes automatically once the call ends."
                : "Dial one number now to verify your calling setup. Message plays once via built-in text-to-speech, then hangs up."}
            </DialogDescription>
          </DialogHeader>

          {!tracking ? (
            <div className="space-y-3 py-2">
              <Field label="Destination (E.164)">
                <Input placeholder="+15871234567" value={to}
                  onChange={(e) => { setTo(e.target.value); if (wasPrefilled) setWasPrefilled(false); }}
                  data-testid="ai-outbound-test-call-to" />
                {wasPrefilled && to && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Remembered from your last test call. <button className="underline hover:text-foreground"
                      onClick={() => { setTo(""); setWasPrefilled(false); try { localStorage.removeItem(LAST_TEST_TO_KEY); } catch { /* private mode */ } }}
                      data-testid="ai-outbound-test-call-clear-to" type="button">Clear</button>
                  </div>
                )}
              </Field>
              <Field label="Message to speak">
                <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                  data-testid="ai-outbound-test-call-msg" />
              </Field>
            </div>
          ) : (
            <CallStatusView tracking={tracking} />
          )}

          <DialogFooter>
            {tracking ? (
              <Button variant="outline" onClick={() => closeDialog(false)} data-testid="ai-outbound-test-call-close">
                {tracking.is_terminal ? "Close" : "Close"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="ai-outbound-test-call-cancel">Cancel</Button>
                <Button onClick={place} disabled={placing || !to.trim()} data-testid="ai-outbound-test-call-place"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Call now
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OutboundPauseButton() {
  const [paused, setPaused] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/outbound/settings").then((r) => setPaused(!!r.data?.paused)).catch(() => setPaused(false));
  }, []);
  const toggle = async () => {
    if (paused == null) return;
    setSaving(true);
    try {
      const target = !paused;
      const { data } = await api.post(`/outbound/settings/pause?paused=${target}`);
      setPaused(!!data.paused);
      toast.success(data.paused ? "Outbound dialer paused for your org." : "Outbound dialer resumed.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Button variant="outline" onClick={toggle} disabled={saving || paused == null}
      data-testid="ai-outbound-pause-toggle"
      className={paused ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100" : ""}
      title={paused ? "Outbound is PAUSED — the scheduler and manual dial are both blocked." : "Kill switch — pauses the auto-dialer AND blocks manual dial for the whole org."}>
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />)}
      {paused ? "Resume outbound" : "Pause outbound"}
    </Button>
  );
}

export function ExportCallHistoryButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/outbound/calls-live.csv");
      if (!data?.token) throw new Error("no token");
      await downloadFromUrl(`/files/${data.token}`);
      toast.success(`Exported ${data.count} call(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={download} disabled={busy}
      data-testid="ai-outbound-export-call-history"
      title="Download every real-call attempt as CSV (answered · voicemail · no-answer · busy · failed · DNC-skipped · canceled).">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      Call history CSV
    </Button>
  );
}

export function BulkRunButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("Run every ACTIVE campaign whose schedule allows a call right now?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/outbound/campaigns/run-all-active");
      setResult(data);
      toast.success(`Bulk run: ${data.ran} ran, ${data.skipped} skipped${data.errored ? `, ${data.errored} errored` : ""}`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={run} disabled={running} data-testid="ai-outbound-bulk-run-btn">
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        {running ? "Running…" : "Run all active now"}
      </Button>
      {result && (
        <Dialog open onOpenChange={(v) => !v && setResult(null)}>
          <DialogContent className="max-w-lg" data-testid="ai-outbound-bulk-run-result">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Play className="h-4 w-4 text-primary" /> Bulk run complete</DialogTitle>
              <DialogDescription>
                {result.ran} ran · {result.skipped} skipped{result.errored ? ` · ${result.errored} errored` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {(result.results || []).map((r) => (
                    <tr key={r.campaign_id} data-testid={`ai-outbound-bulk-row-${r.campaign_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{r.name}</div>
                        {r.reason && <div className="text-xs text-slate-500">{r.reason}</div>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          r.status === "ran" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : r.status === "skipped" ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-red-200 bg-red-50 text-red-700"}`}>
                          {r.status}{r.attempts ? ` · ${r.attempts}` : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(result.results || []).length === 0 && (
                    <tr><td className="px-3 py-6 text-center text-xs text-slate-400">No campaigns matched. Toggle at least one campaign to Active and check its schedule.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
